import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Tabs,
  Tab,
  Paper,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Search, Clear } from "@mui/icons-material";
import StitchChip from "./StitchChip";
import {
  getSymbolsByCategory,
  getSymbolsUsedInPattern,
  getAllSymbols,
} from "../../utils/stitchSymbols";
import "./StitchPalette.css";

const StitchPalette = ({
  selectedSymbol,
  onSymbolSelect,
  chart = [],
  onSelectedStitchClickRef,
}) => {
  const [activeTab, setActiveTab] = useState(2);
  const [searchQuery, setSearchQuery] = useState("");

  // Handle selected stitch click to switch to the appropriate tab
  const handleSelectedStitchClick = useCallback(() => {
    if (!selectedSymbol) return;

    const allSymbols = getAllSymbols();
    const symbolData = allSymbols.find((s) => s.symbol === selectedSymbol);

    if (symbolData) {
      const categoryMap = {
        Basics: 2,
        Increases: 3,
        Decreases: 4,
        Cables: 5,
        Lace: 6,
        Texture: 7,
        Other: 8,
      };

      const tabIndex = categoryMap[symbolData.category] || 2;
      setActiveTab(tabIndex);
      setSearchQuery("");
    }
  }, [selectedSymbol]);

  // Expose the click handler to parent
  useEffect(() => {
    if (onSelectedStitchClickRef) {
      onSelectedStitchClickRef(handleSelectedStitchClick);
    }
  }, [onSelectedStitchClickRef, handleSelectedStitchClick]);

  // Get all symbols organized by category
  const symbolCategories = getSymbolsByCategory();
  const patternSymbols = getSymbolsUsedInPattern(chart);
  const allSymbols = getAllSymbols();

  // Filter symbols based on search query
  const filteredSymbols = useMemo(() => {
    if (!searchQuery.trim()) {
      return [];
    }

    const query = searchQuery.toLowerCase();
    return allSymbols.filter(
      (symbol) =>
        (symbol.abbreviation &&
          symbol.abbreviation.toLowerCase().includes(query)) ||
        (symbol.meaning && symbol.meaning.toLowerCase().includes(query)) ||
        (symbol.description &&
          symbol.description.toLowerCase().includes(query)) ||
        (symbol.officialName &&
          symbol.officialName.toLowerCase().includes(query)),
    );
  }, [searchQuery, allSymbols]);

  // Create tab data structure
  const tabData = [
    {
      label: "Recent",
      symbols: patternSymbols,
      isEmpty: patternSymbols.length === 0,
      emptyMessage: "Start adding stitches to your pattern",
    },
    {
      label: "Search",
      symbols: filteredSymbols,
      isEmpty: filteredSymbols.length === 0,
      emptyMessage: "Type to search for stitches...",
      isSearchTab: true,
    },
    {
      label: "Basics",
      symbols: symbolCategories.Basics || [],
    },
    {
      label: "Increases",
      symbols: symbolCategories.Increases || [],
    },
    {
      label: "Decreases",
      symbols: symbolCategories.Decreases || [],
    },
    {
      label: "Cables",
      symbols: symbolCategories.Cables || [],
    },
    {
      label: "Lace",
      symbols: symbolCategories.Lace || [],
    },
    {
      label: "Texture",
      symbols: symbolCategories.Texture || [],
    },
    {
      label: "More",
      symbols: symbolCategories.Other || [],
    },
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Only clear search if switching away from search tab
    if (newValue !== 1) {
      setSearchQuery("");
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    // Switch to search tab when user starts typing
    if (event.target.value.trim() && activeTab !== 1) {
      setActiveTab(1);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setActiveTab(2); // Switch back to Basics tab (default)
  };

  const currentSymbols = tabData[activeTab]?.symbols || [];
  const isSearchTab = tabData[activeTab]?.isSearchTab || false;

  return (
    <Paper variant="woven" className="stitch-palette">
      <Box className="stitch-palette__header">
        <Typography
          variant="h5"
          color="primary"
          className="stitch-palette__title"
        >
          Select a stitch
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          className="stitch-palette__instruction"
        >
          Select a stitch, then click a square or click-and-drag to apply.
        </Typography>
      </Box>

      {/* Tab Navigation */}
      <Box className="stitch-palette__tabs-container">
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          className="stitch-palette__tabs"
          aria-label="stitch categories"
        >
          {tabData.map((tab, index) => (
            <Tab
              key={tab.label}
              label={tab.label}
              className="stitch-palette__tab"
              disabled={tab.isEmpty && index === 0}
            />
          ))}
        </Tabs>
      </Box>

      {/* Search Field (only for Search tab) */}
      {isSearchTab && (
        <Box className="stitch-palette__search-container">
          <TextField
            fullWidth
            placeholder="Search stitches..."
            value={searchQuery}
            onChange={handleSearchChange}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    edge="end"
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            className="stitch-palette__search-field"
          />
        </Box>
      )}

      {/* Symbol Grid */}
      <Box className="stitch-palette__grid">
        {currentSymbols.length === 0 ? (
          <Box className="stitch-palette__empty">
            <Typography variant="body2" color="text.secondary">
              {isSearchTab && searchQuery.trim()
                ? "No stitches found"
                : tabData[activeTab]?.emptyMessage || "No symbols available"}
            </Typography>
          </Box>
        ) : (
          currentSymbols.map((symbol, index) => (
            <StitchChip
              key={`${symbol.symbol}-${index}`}
              symbol={symbol}
              selected={selectedSymbol === symbol.symbol}
              onSelect={onSymbolSelect}
              size="medium"
            />
          ))
        )}
      </Box>
    </Paper>
  );
};

export default StitchPalette;
